import React, {useEffect, useState} from "react";
import ReactGridLayout, {WidthProvider} from "react-grid-layout";
import {useMediaQuery} from "react-responsive";
import {useDispatch} from "react-redux";
import PropTypes from "prop-types";
import {updateDashboardCells} from "../../../../redux/Actions/tsdb.actions";
import {fastMap} from "../../Helpers/fastMap";
import Cell from "./Cell";
import "./react-grid-layout.scss";

const Grid = WidthProvider(ReactGridLayout);

/** This component uses react-grid-layout library to render cell component in grid : https://www.npmjs.com/package/react-grid-layout */

const calculateLayout = (cells, isMobile) => {
  const defaultHeight = 4;

  return cells.map((cell = {}, index) => {
    return {
      ...cell,
      x: cell.x ?? 0,
      y: cell.y ?? index * defaultHeight,
      w: isMobile ? 12 : (cell.w ?? 12),
      h: cell.h ?? defaultHeight,
      i: cell.id,
    };
  });
};

const Cells = (props) => {
  const {
    cells,
    dashboardId,
    isDraggable = true,
    isResizable = true,
    timeRangeStart,
    timeRangeStop,
    ...rest
  } = props;
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({query: "(max-width: 500px)"});
  const [layout, setLayout] = useState([]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLayout(calculateLayout(cells, isMobile));
  }, [cells, isMobile]);

  const handleLayoutChange = (grid) => {
    if (isDraggable || isResizable) {
      let changed = false;

      const newLayout = layout.map((cell) => {
        const l = grid?.find((ly) => ly?.i === cell?.id);

        if (
          cell?.x !== l?.x ||
          cell?.y !== l?.y ||
          cell?.h !== l?.h ||
          cell?.w !== l?.w
        ) {
          changed = true;
        }

        const newCell = {
          x: l?.x,
          y: l?.y,
          h: l?.h,
          w: l?.w,
        };

        return {
          ...cell,
          ...newCell,
        };
      });

      if (changed) {
        setLayout(newLayout);
        handlePositionChange(newLayout);
      }
    }
  };

  const handlePositionChange = (cells) => {
    dispatch(updateDashboardCells({id: dashboardId, cells}));
  };

  return (
    <Grid
      cols={12}
      layout={layout}
      rowHeight={83.5}
      useCSSTransforms={true}
      containerPadding={[0, 0]}
      margin={[4, 4]}
      draggableHandle=".cell--draggable"
      onDragStop={handleLayoutChange}
      onResizeStop={handleLayoutChange}
      isResizable={isMobile ? false : isResizable}
      isDraggable={isDraggable}
    >
      {fastMap(cells, (cell = {}) => {
        const {id, properties = {}} = cell;
        return (
          <div key={id} className="cell">
            <Cell
              cell={cell}
              timeRangeStart={getActiveTimeRange(timeRangeStart, properties.queries)}
              timeRangeStop={getActiveTimeRange(timeRangeStop, properties.queries)}
              {...rest}
            />
          </div>
        );
      })}
    </Grid>
  );
};

Cells.propTypes = {
  dashboardId: PropTypes.string.isRequired,
  cells: PropTypes.arrayOf(PropTypes.object).isRequired,
  isResizable: PropTypes.bool,
  isDraggable: PropTypes.bool,
  timeRangeStart: PropTypes.string,
  timeRangeStop: PropTypes.string,
};

export default Cells;

const getActiveTimeRange = (timeRange, queries) => {
  if (!queries) {
    return timeRange;
  }
  const hasVariableTimes = queries.some(
    (query) =>
      query.text.includes("v.timeRangeStart") || query.text.includes("v.timeRangeStop")
  );
  if (hasVariableTimes) {
    return timeRange;
  }
  return null;
};
